import { View } from "@pimo/pimo-app-builder";
import {
  DragAndDropLayout,
  PMProgressBar,
  PMSmallNumber,
  PMSmallNumberCardEditSchema,
} from "@pimo/pimo-components";
import { APP_RECORD_TYPES } from "vmd-types";

import { TestAppState } from "../../../app";
import {
  countReportingItemsByRecordType,
  isRecordTypeVisible,
} from "../../../utils/filter-reporting-items-by-record-type";
import {
  criticalityLevelSet,
  getAllReportingItems,
} from "../get-dashboard-chart-data";

/**
 * Adds the DC business apps, reviews status, IT services (conditional) and
 * critical DC Business Apps number cards, in that order (#2222).
 *
 * The IT services card is only added when
 * `isRecordTypeVisible(APP_RECORD_TYPES.IT_SERVICE)` is true — it is not
 * merely hidden, it is not registered at all, exactly as the original
 * `buildOverview()` did inside its own `if`.
 * `overview-plugin.structure.spec.ts` asserts this conditionally, too.
 */
export function addBusinessAppNumberCards(
  view: View<TestAppState, DragAndDropLayout>
) {
  const numberOfBusinessApplicationsComponent = view.addComponent({
    component: PMSmallNumber,
    layoutProps: {
      componentId: "numberOfBusinessApplicationsDashboardComponent",
      labels: ["#ofBusinessApplications"],
      description: "Shows the number of DC Business Apps across all projects.",
      title: "DC Business Apps",
      editSchema: PMSmallNumberCardEditSchema,
    },
  });

  numberOfBusinessApplicationsComponent.mapState(({ dashboard }) => {
    const allItems = getAllReportingItems(dashboard);
    return {
      title: "DC Business Apps",
      number: countReportingItemsByRecordType(
        allItems,
        APP_RECORD_TYPES.BUSINESS_APPLICATION
      ),
    };
  });

  const reviewsStatusComponent = view.addComponent({
    component: PMProgressBar,
    layoutProps: {
      componentId: "reviewsStatusDashboardComponent",
      labels: ["reviewsStatus", "progress"],
      description: "Shows submitted reviews across all Business Applications.",
      title: "Reviews Status",
    },
  });

  reviewsStatusComponent.mapState(({ dashboard, overview }) => ({
    icon: "TrendingUp",
    title: "Reviews Status",
    label: "Reviews Submitted",
    value:
      overview?._calculated?.reviewedBusinessApplications ??
      dashboard?._calculated?.reviewedBusinessApplications ??
      0,
    total:
      overview?._calculated?.totalBusinessApplications ??
      dashboard?._calculated?.totalBusinessApplications ??
      0,
    percent:
      overview?._calculated?.reviewedBusinessApplicationsPercent ??
      dashboard?._calculated?.reviewedBusinessApplicationsPercent ??
      0,
  }));

  if (isRecordTypeVisible(APP_RECORD_TYPES.IT_SERVICE)) {
    const numberOfITServicesComponent = view.addComponent({
      component: PMSmallNumber,
      layoutProps: {
        componentId: "numberOfITServicesDashboardComponent",
        labels: ["#ofITServices"],
        description: "Shows the number of IT Services across all projects.",
        title: "IT Services",
        editSchema: PMSmallNumberCardEditSchema,
      },
    });

    numberOfITServicesComponent.mapState(({ dashboard }) => {
      const allItems = getAllReportingItems(dashboard);
      return {
        title: "IT Services",
        number: countReportingItemsByRecordType(
          allItems,
          APP_RECORD_TYPES.IT_SERVICE
        ),
      };
    });
  }

  const criticalDCSystemsComponent = view.addComponent({
    component: PMSmallNumber,
    layoutProps: {
      componentId: "criticalDCSystemsDashboardComponent",
      labels: ["#criticalDCSystems"],
      description:
        "Shows the number of critical DC Business Apps across all projects.",
      title: "Critical DC Business Apps",
      editSchema: PMSmallNumberCardEditSchema,
    },
  });

  criticalDCSystemsComponent.mapState(({ dashboard }) => {
    const allItems = getAllReportingItems(dashboard);
    const criticalCount = allItems.filter(
      (item) =>
        !!item?.basicItem?.criticality &&
        criticalityLevelSet.has(item.basicItem.criticality)
    ).length;

    return {
      title: "Critical DC Business Apps",
      number: criticalCount,
    };
  });
}
